import { computed } from 'vue';
import { CORE_ADMIN, grantableRoles } from '@dashboard/module-contract/manifest';
import { BUNDLED } from './modules/registry';
import { hasRole } from './session';
import type { Viewer } from '@dashboard/ui-kit';
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router';
import type { BundledModule } from './modules/registry-types';

/**
 * The core's side of the viewer a module view injects.
 *
 * The scope is decided here rather than by the module, which is the point: a
 * view is answered about the roles it could legitimately have declared — its
 * own module's, plus `core:admin` — and nothing else. That is the same set
 * `grantableRoles` gives the manifest validator, so a module can ask about
 * exactly what it can require, and the two cannot drift.
 *
 * Which module is on screen comes from `meta.moduleId`, written by
 * `moduleRoutes`. A core page has none, and scopes to `core:admin` alone.
 */
export function createViewer(
	route: RouteLocationNormalizedLoaded,
	router: Router,
	modules: readonly BundledModule[] = BUNDLED,
): Viewer {
	const manifests = new Map(modules.map(({ manifest }) => [manifest.id, manifest]));

	const scope = computed<ReadonlySet<string>>(() => {
		const moduleId = route.meta.moduleId;
		const manifest = moduleId === undefined ? undefined : manifests.get(moduleId);
		return new Set<string>(manifest === undefined ? [CORE_ADMIN] : grantableRoles(manifest));
	});

	return {
		can: (role) => scope.value.has(role) && hasRole(role),
		// Read during render, which is what makes them reactive. Both would be
		// wrong by the time anything acted on a copy.
		signInHref: () => router.resolve({ name: 'sign-in', query: { next: route.fullPath } }).href,
	};
}
